/**
 * Generic Cache Helper for RTK Query Optimistic Updates
 * 
 * This module provides reusable functions for performing optimistic updates
 * on RTK Query caches without triggering refetches.
 */

/**
 * Generic function to perform optimistic updates on both single item and list caches
 */
export function performOptimisticUpdate(config, payload, dispatch, getState) {
  const patches = [];

  // Update single item cache if it exists
  try {
    const itemId = config.getItemId ? config.getItemId(payload) : payload.id;
    
    const singleItemPatch = dispatch(
      config.api.util.updateQueryData(config.singleItemQueryName, itemId, (draft) => {
        console.log(`Updating ${config.singleItemQueryName} cache for item:`, itemId);
        config.updateItem(draft, payload);
      })
    );
    patches.push(singleItemPatch);
  } catch (error) {
    console.log(`No cached data found for ${config.singleItemQueryName}`, error);
  }

  // Update all list queries in cache
  try {
    const state = getState();
    const listQueries = config.api.util.selectCachedArgsForQuery(state, config.listQueryName);
    
    listQueries.forEach((queryArgs) => {
      const listPatch = dispatch(
        config.api.util.updateQueryData(config.listQueryName, queryArgs, (draft) => {
          console.log(`Updating ${config.listQueryName} cache for args:`, queryArgs);
          
          // Handle different list response structures
          const items = draft.notifications || draft.files || draft.items || draft.data || draft;
          
          if (Array.isArray(items)) {
            const itemIndex = items.findIndex((item) => 
              config.matchItem(item, payload)
            );
            if (itemIndex !== -1) {
              console.log(`Updating item at index ${itemIndex} with payload:`, payload);
              config.updateItem(items[itemIndex], payload);
            }
          }
        })
      );
      patches.push(listPatch);
    });
  } catch (error) {
    console.log(`Error updating ${config.listQueryName} cache:`, error);
  }

  return patches;
}

/**
 * Reverts all optimistic updates
 */
export function revertOptimisticUpdates(patches) {
  patches.forEach(patch => {
    try {
      patch.undo();
    } catch (error) {
      console.error('Error reverting optimistic update:', error);
    }
  });
}

/**
 * Creates a generic onQueryStarted handler for mutations that need optimistic updates
 */
export function createOptimisticUpdateHandler(config) {
  return async function onQueryStarted(
    payload,
    { dispatch, queryFulfilled, getState }
  ) {
    console.log(`Performing optimistic update for ${config.singleItemQueryName}/${config.listQueryName}:`, payload);
    
    // Perform optimistic updates
    const patches = performOptimisticUpdate(config, payload, dispatch, getState);

    try {
      // Wait for the mutation to complete
      await queryFulfilled;
      console.log('Mutation completed successfully:', payload);
    } catch (error) {
      console.log('Mutation failed, reverting optimistic updates:', error);
      // Revert all optimistic updates on failure
      revertOptimisticUpdates(patches);
    }
  };
}

/**
 * Default configuration helpers for common patterns
 */
export const defaultConfigs = {
  /**
   * Standard configuration for entities with id field
   */
  withId: (api, singleItemQueryName, listQueryName) => ({
    api,
    singleItemQueryName,
    listQueryName,
    getItemId: (payload) => payload.id,
    matchItem: (item, payload) => item.id === payload.id,
    updateItem: (item, payload) => Object.assign(item, payload),
  }),

  /**
   * Configuration for entities with custom ID field
   */
  withCustomId: (api, singleItemQueryName, listQueryName, idField) => ({
    api,
    singleItemQueryName,
    listQueryName,
    getItemId: (payload) => payload[idField],
    matchItem: (item, payload) => item[idField] === payload[idField],
    updateItem: (item, payload) => Object.assign(item, payload),
  }),

  /**
   * Configuration for notifications with custom update logic matching actual API response
   */
  forNotifications: (api) => ({
    api,
    singleItemQueryName: 'getNotification',
    listQueryName: 'getNotifications',
    getItemId: (payload) => payload.id,
    matchItem: (item, payload) => item.id === payload.id,
    updateItem: (item, payload) => {
      // Handle notification-specific updates based on actual API structure
      if (payload.action === 'mark_read') {
        item.status = 'read';
        item.read_at = new Date().toISOString();
        item.updated_at = new Date().toISOString();
      } else if (payload.action === 'mark_unread') {
        item.status = 'unread';
        item.read_at = null;
        item.updated_at = new Date().toISOString();
      } else {
        // For other updates, merge the payload
        Object.assign(item, payload);
        // Always update the updated_at timestamp
        item.updated_at = new Date().toISOString();
      }
    },
  }),
}; 